import React from 'react';
import * as Icons from 'lucide-react';
import { DEFAULT_SITE_CONTENT, mergeContent } from '@/constants/defaultSiteContent';

// Speed presets translate to animation duration. Adjustable via CMS.
const SPEED_SECONDS = { slow: 55, medium: 32, fast: 18 };

// Theme swatches — background, foreground text, accent, border.
const THEMES = {
  green: {
    bg: 'bg-[#0F2E23]',
    fg: 'text-[#F5F0E6]',
    accent: 'text-[#F5C86A]',
    border: 'border-y border-[#F5C86A]/20',
  },
  cream: {
    bg: 'bg-[#F5F0E6]',
    fg: 'text-[#0F2E23]',
    accent: 'text-[#0F2E23]',
    border: 'border-y border-[#0F2E23]/15',
  },
  gold: {
    bg: 'bg-[#F5C86A]',
    fg: 'text-[#0F2E23]',
    accent: 'text-[#0F2E23]',
    border: 'border-y border-[#0F2E23]/20',
  },
};

const SEP = {
  diamond: '◆',
  dot: '•',
  slash: '/',
  none: '',
};

const renderItem = (item, i, theme) => {
  const IconComp = item?.icon ? Icons[item.icon] : null;
  return (
    <span key={`m-${i}-${item?.text || ''}`} className={`inline-flex items-center gap-3 shrink-0 ${theme.fg}`}>
      {item?.image_url ? (
        <span className="inline-flex w-7 h-7 rounded-full overflow-hidden ring-1 ring-current/20">
          <img src={item.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
        </span>
      ) : IconComp ? (
        <IconComp className={`w-4 h-4 ${theme.accent}`} strokeWidth={1.8} />
      ) : null}
      <span className="font-display text-[15px] sm:text-lg tracking-[0.22em] whitespace-nowrap">{item?.text}</span>
    </span>
  );
};

const Marquee = ({ content }) => {
  const c = mergeContent(DEFAULT_SITE_CONTENT.marquee, content || {});
  if (c?.enabled === false) return null;
  const items = (c.items || []).filter((it) => it && (it.text || it.image_url));
  if (!items.length) return null;

  const theme = THEMES[c.theme] || THEMES.green;
  const durationSec = SPEED_SECONDS[c.speed] || SPEED_SECONDS.medium;
  const dir = c.direction === 'right' ? 'reverse' : 'normal';
  const sepChar = SEP[c.separator] ?? SEP.diamond;

  // We duplicate the strip twice for a seamless infinite loop.
  const strip = (
    <div className="marquee-strip inline-flex items-center gap-8 pr-8">
      {items.map((it, i) => (
        <React.Fragment key={i}>
          {renderItem(it, i, theme)}
          {sepChar && <span className={`${theme.accent} opacity-60 text-sm shrink-0`} aria-hidden>{sepChar}</span>}
        </React.Fragment>
      ))}
    </div>
  );

  return (
    <section
      aria-label="Announcements"
      data-testid="public-marquee"
      className={`relative overflow-hidden ${theme.bg} ${theme.border}`}
    >
      {/* Soft edge masks so content fades in/out */}
      <div className="pointer-events-none absolute inset-y-0 left-0 w-16 z-10" style={{ background: `linear-gradient(90deg, ${c.theme === 'cream' ? 'rgba(245,240,230,1)' : c.theme === 'gold' ? 'rgba(245,200,106,1)' : 'rgba(15,46,35,1)'}, transparent)` }} />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-16 z-10" style={{ background: `linear-gradient(270deg, ${c.theme === 'cream' ? 'rgba(245,240,230,1)' : c.theme === 'gold' ? 'rgba(245,200,106,1)' : 'rgba(15,46,35,1)'}, transparent)` }} />

      <div
        className="marquee-track flex w-max py-3 sm:py-4"
        style={{ animation: `pg-marquee-scroll ${durationSec}s linear infinite ${dir}` }}
      >
        {strip}
        {strip}
      </div>

      <style>{`
        @keyframes pg-marquee-scroll {
          from { transform: translate3d(0, 0, 0); }
          to   { transform: translate3d(-50%, 0, 0); }
        }
        .marquee-track:hover { animation-play-state: paused; }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track { animation: none !important; }
        }
      `}</style>
    </section>
  );
};

export default Marquee;
